import {
  IconArrowUpRightOutline18,
  IconBellOutline18,
  IconCheckOutline18,
  IconClockOutline18,
  IconCodeOutline18,
  IconCrosshairsOutline18,
  IconCurrencyDollarOutline18,
  IconEarthOutline18,
  IconServerOutline18,
} from "nucleo-ui-outline-18";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EnhancedJsonViewWithCopy } from "@/components/ui/enhanced-json-view";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { Button } from "@/components/ui/button";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { useNodeStatistics } from "@/hooks/useWorkflowStatistics";
import type { X402Endpoint } from "@/hooks/useX402Endpoints";
import { formatX402Price } from "@/hooks/useX402Endpoints";
import { formatNumber } from "@/lib/utils/formatNumber";
import { useConnectorsStore } from "@/stores/connectorsStore";
import Link from "next/link";

// Helper to format example values for display
function formatExampleValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "";
  if (typeof value === "string") return `"${value}"`;
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      return `[${value.length} items]`;
    }
    const keys = Object.keys(value);
    if (keys.length === 0) return "{}";
    return `{${keys.slice(0, 3).join(", ")}${keys.length > 3 ? ", ..." : ""}}`;
  }
  return String(value);
}

// Schema field component for displaying input/output fields
function SchemaField({
  fieldName,
  fieldSchema,
  isRequired,
  variant = "input",
  isLast = false,
  depth = 0,
  exampleValue,
}: {
  fieldName: string;
  fieldSchema: any;
  isRequired: boolean;
  variant?: "input" | "output";
  isLast?: boolean;
  depth?: number;
  exampleValue?: unknown;
}) {
  const bgColor = variant === "input" ? "from-blue-50 to-blue-100" : "from-green-50 to-green-100";
  const badgeColor =
    variant === "input"
      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
      : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";

  // Check if this field has nested properties
  const nestedProperties = fieldSchema.properties as Record<string, any> | undefined;
  const nestedRequired = (fieldSchema.required || []) as string[];
  const hasNestedFields = nestedProperties && Object.keys(nestedProperties).length > 0;

  return (
    <AccordionItem
      value={`${fieldName}-${depth}`}
      className={isLast ? "border-b-0" : "border-ds-border-default border-b"}
    >
      <AccordionTrigger className="py-3 hover:no-underline">
        <div className="flex w-full items-center justify-between pr-4">
          <div className="flex items-center gap-2">
            <code
              className={`rounded bg-gradient-to-br ${bgColor} text-ds-text-primary dark:from-ds-neutral-700 dark:to-ds-neutral-700 px-2 py-0.5 font-mono text-xs font-semibold`}
            >
              {fieldName}
            </code>
            {isRequired && (
              <Badge variant="destructive" className="micro-label gap-1.5">
                <IconCheckOutline18 className="h-2.5 w-2.5" />
                Required
              </Badge>
            )}
          </div>
          <Badge variant={variant === "input" ? "info" : "success"}>{fieldSchema.type || "string"}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-2 pb-3">
        {fieldSchema.description && (
          <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{fieldSchema.description}</p>
        )}

        {fieldSchema.enum && (
          <div>
            <div className="micro-label mb-1">Allowed values:</div>
            <div className="flex flex-wrap gap-1">
              {fieldSchema.enum.slice(0, 5).map((value: string, index: number) => (
                <code
                  key={index}
                  className="bg-ds-neutral-200 text-ds-text-secondary dark:bg-ds-neutral-700 text-3xs rounded px-1.5 py-0.5 font-mono"
                >
                  {String(value)}
                </code>
              ))}
              {fieldSchema.enum.length > 5 && <span className="micro-label">+{fieldSchema.enum.length - 5} more</span>}
            </div>
          </div>
        )}

        {fieldSchema.default !== undefined && (
          <div className="micro-label">
            <span>Default:</span>{" "}
            <code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1 py-0.5 font-mono">
              {String(fieldSchema.default)}
            </code>
          </div>
        )}

        {exampleValue !== undefined && formatExampleValue(exampleValue) && (
          <div className="micro-label">
            <span>Example:</span>{" "}
            <code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1 py-0.5 font-mono">
              {formatExampleValue(exampleValue)}
            </code>
          </div>
        )}

        {/* Nested properties for objects */}
        {hasNestedFields && depth < 2 && (
          <div className="border-ds-border-default mt-2 border-l-2 pl-3">
            <Accordion type="multiple" className="w-full">
              {Object.entries(nestedProperties).map(([nestedFieldName, nestedFieldSchema], index, arr) => {
                // Get nested example value if parent example is an object
                const nestedExampleValue =
                  exampleValue && typeof exampleValue === "object" && !Array.isArray(exampleValue)
                    ? (exampleValue as Record<string, unknown>)[nestedFieldName]
                    : undefined;
                return (
                  <SchemaField
                    key={nestedFieldName}
                    fieldName={nestedFieldName}
                    fieldSchema={nestedFieldSchema}
                    isRequired={nestedRequired.includes(nestedFieldName)}
                    variant={variant}
                    isLast={index === arr.length - 1}
                    depth={depth + 1}
                    exampleValue={nestedExampleValue}
                  />
                );
              })}
            </Accordion>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

// Extended node type that includes x402 tracking fields
type ExtendedActionTriggerDefinition = ActionTriggerDefinition & {
  _isX402?: boolean;
  _endpoint?: X402Endpoint;
};

// X402-specific content component matching original modal style
function X402InfoContent({
  node,
  showHeader = true,
  showDetailsLink = true,
  onDetailsClick,
}: {
  node: ExtendedActionTriggerDefinition;
  showHeader?: boolean;
  showDetailsLink?: boolean;
  onDetailsClick?: () => void;
}) {
  const endpoint: X402Endpoint | undefined = node._endpoint;
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const connectorLogoUrl = node.connector?.type
    ? connectorTypes.find(ct => ct.type === node.connector?.type)?.logoUrl
    : undefined;
  const connectorProp = node.connector ? { type: node.connector.type, logoUrl: connectorLogoUrl } : undefined;

  // Separate system input (x-hidden fields) from user input
  const allInputFields = node.payloadSchema?.properties || {};
  const systemInputFields: Record<string, any> = {};
  const userInputFields: Record<string, any> = {};

  for (const [fieldName, fieldSchema] of Object.entries(allInputFields)) {
    const schema = fieldSchema as Record<string, unknown>;
    if (schema["x-hidden"] || schema.readOnly) {
      systemInputFields[fieldName] = fieldSchema;
    } else {
      userInputFields[fieldName] = fieldSchema;
    }
  }

  // Get required fields
  const requiredInputFields = node.payloadSchema?.required || [];
  const userRequiredCount = Object.keys(userInputFields).filter(
    f => requiredInputFields.includes(f) || (userInputFields[f] as any)["x-required"],
  ).length;

  // Separate system output from resource output
  const allOutputFields = (node.resultSchema?.properties || {}) as Record<string, any>;
  const systemOutputFieldNames = ["status", "statusCode", "paymentDetails", "requestDetails"];
  const systemOutputFields: Record<string, any> = {};
  const resourceOutputFields: Record<string, any> = {};

  for (const [fieldName, fieldSchema] of Object.entries(allOutputFields)) {
    if (systemOutputFieldNames.includes(fieldName)) {
      systemOutputFields[fieldName] = fieldSchema;
    } else {
      resourceOutputFields[fieldName] = fieldSchema;
    }
  }

  const outputRequiredFields = node.resultSchema?.required || [];

  // Get example output from endpoint
  const exampleOutput = endpoint?.exampleOutput;

  return (
    <div className="space-y-4">
      {/* Header */}
      {showHeader && (
        <div className="flex items-start gap-4">
          <NodeIconTile
            size="md"
            id={node.type}
            subject={node.name}
            context={node.description}
            logoUrl={node.logoUrl}
            connector={connectorProp}
            alt={node.name}
            color="#2450FF"
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-ds-text-primary text-lg font-semibold">{node.name}</h3>
            <span className="text-ds-text-muted text-sm">x402 Action</span>
          </div>
        </div>
      )}
      {/* Description */}
      <p className="body-sm">{node.description}</p>
      {/* Endpoint metadata badges */}
      {endpoint && (
        <div className="flex flex-wrap gap-2">
          <Badge variant="success" className="px-2.5 py-1">
            <IconCurrencyDollarOutline18 className="h-3 w-3" />
            {formatX402Price(endpoint.price, endpoint.tokenDecimals)} {endpoint.tokenSymbol || "USDC"}
          </Badge>
          <Badge variant="info" className="px-2.5 py-1">
            <IconEarthOutline18 className="h-3 w-3" />
            {endpoint.network}
          </Badge>
          <Badge variant="purple" className="px-2.5 py-1">
            <IconServerOutline18 className="h-3 w-3" />
            {endpoint.method}
          </Badge>
        </div>
      )}
      {/* URL */}
      {endpoint?.baseUrl ? (
        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded-lg p-2">
          <code className="text-ds-text-subtle dark:text-ds-text-disabled break-all text-xs">{endpoint.baseUrl}</code>
        </div>
      ) : null}
      {/* Connector Requirement */}
      {node.connector && (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
          <IconCodeOutline18 className="h-4 w-4 flex-shrink-0" />
          <span>
            Requires{" "}
            <span className="font-medium">
              {node.connector.type.charAt(0).toUpperCase() + node.connector.type.slice(1)}
            </span>{" "}
            connector
          </span>
        </div>
      )}
      {/* User Input Fields */}
      {Object.keys(userInputFields).length > 0 && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">
            Inputs{" "}
            <span className="text-ds-text-muted font-normal normal-case tracking-normal">
              ({Object.keys(userInputFields).length}
              {userRequiredCount > 0 && `, ${userRequiredCount} required`})
            </span>
          </h4>
          <Accordion type="multiple" className="w-full">
            {Object.entries(userInputFields).map(([fieldName, fieldSchema], index, arr) => {
              const schema = fieldSchema as Record<string, unknown>;
              const isRequired = schema["x-required"] || requiredInputFields.includes(fieldName);
              const displayName = (schema.title as string) || fieldName;
              return (
                <SchemaField
                  key={fieldName}
                  fieldName={displayName}
                  fieldSchema={fieldSchema}
                  isRequired={isRequired as boolean}
                  variant="input"
                  isLast={index === arr.length - 1}
                />
              );
            })}
          </Accordion>
        </div>
      )}
      {/* System Input Fields */}
      {Object.keys(systemInputFields).length > 0 && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">
            System Inputs{" "}
            <span className="text-ds-text-muted font-normal normal-case tracking-normal">
              ({Object.keys(systemInputFields).length})
            </span>
          </h4>
          <p className="label-xs mb-2">Internal fields automatically populated by the system.</p>
          <Accordion type="multiple" className="w-full">
            {Object.entries(systemInputFields).map(([fieldName, fieldSchema], index, arr) => (
              <SchemaField
                key={fieldName}
                fieldName={fieldName}
                fieldSchema={fieldSchema}
                isRequired={requiredInputFields.includes(fieldName)}
                variant="input"
                isLast={index === arr.length - 1}
              />
            ))}
          </Accordion>
        </div>
      )}
      {/* Resource Output Fields (the actual endpoint data) */}
      {Object.keys(resourceOutputFields).length > 0 && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">
            Outputs{" "}
            <span className="text-ds-text-muted font-normal normal-case tracking-normal">
              ({Object.keys(resourceOutputFields).length})
            </span>
          </h4>
          <Accordion type="multiple" className="w-full">
            {Object.entries(resourceOutputFields).map(([fieldName, fieldSchema], index, arr) => {
              const isRequired = outputRequiredFields.includes(fieldName);
              // Get example value from exampleOutput if available
              const fieldExampleValue =
                exampleOutput && typeof exampleOutput === "object"
                  ? (exampleOutput as Record<string, unknown>)[fieldName]
                  : undefined;
              return (
                <SchemaField
                  key={fieldName}
                  fieldName={fieldName}
                  fieldSchema={fieldSchema}
                  isRequired={isRequired}
                  variant="output"
                  isLast={index === arr.length - 1}
                  exampleValue={fieldExampleValue}
                />
              );
            })}
          </Accordion>
        </div>
      )}
      {/* System Output Fields */}
      {Object.keys(systemOutputFields).length > 0 && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">
            System Outputs{" "}
            <span className="text-ds-text-muted font-normal normal-case tracking-normal">
              ({Object.keys(systemOutputFields).length})
            </span>
          </h4>
          <p className="label-xs mb-2">Metadata about the request and payment.</p>
          <Accordion type="multiple" className="w-full">
            {Object.entries(systemOutputFields).map(([fieldName, fieldSchema], index, arr) => {
              const isRequired = outputRequiredFields.includes(fieldName);
              return (
                <SchemaField
                  key={fieldName}
                  fieldName={fieldName}
                  fieldSchema={fieldSchema}
                  isRequired={isRequired}
                  variant="output"
                  isLast={index === arr.length - 1}
                />
              );
            })}
          </Accordion>
        </div>
      )}
      {/* Example Output */}
      {!!exampleOutput && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">Example Output</h4>
          <div className="emboss-inner bg-ds-neutral-100 dark:bg-ds-neutral-800 max-h-80 overflow-y-auto rounded-lg p-3 font-mono text-xs">
            <EnhancedJsonViewWithCopy value={exampleOutput} defaultExpanded={true} />
          </div>
        </div>
      )}
      {/* Tags Accordion */}
      {node.tags && node.tags.length > 0 && (
        <Accordion type="multiple" className="border-ds-border-default w-full border-t pt-4">
          <AccordionItem value="tags" className="border-b-0">
            <AccordionTrigger className="py-3 hover:no-underline">
              <span className="field-label">
                Tags{" "}
                <span className="text-ds-text-muted font-normal normal-case tracking-normal">({node.tags.length})</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap gap-1.5 pb-2">
                {node.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled rounded-md px-2 py-0.5 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {/* Link to full detail page */}
      {showDetailsLink && (
        <div className="pt-2">
          <Link href={`/integrations/action/${node.type}`} target="_blank" rel="noreferrer" onClick={onDetailsClick}>
            <Button size="sm" className="w-full">
              <span className="flex items-center gap-2 text-xs">
                View full details
                <IconArrowUpRightOutline18 className="h-3 w-3" />
              </span>
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

interface NodeInfoContentProps {
  node: ExtendedActionTriggerDefinition;
  nodeType: "trigger" | "action" | "logic";
  /** Whether to show the header with icon and title */
  showHeader?: boolean;
  /** Whether to show the description */
  showDescription?: boolean;
  /** Whether to show the connector requirement */
  showConnectorRequirement?: boolean;
  /** Whether to show the "View full details" link */
  showDetailsLink?: boolean;
  /** Callback when the details link is clicked */
  onDetailsClick?: () => void;
}

/**
 * Reusable content component for displaying node information.
 * Can be used inside a modal or embedded directly in other components.
 */
export function NodeInfoContent({
  node,
  nodeType,
  showHeader = true,
  showDescription = true,
  showConnectorRequirement = true,
  showDetailsLink = true,
  onDetailsClick,
}: NodeInfoContentProps) {
  // Check if this is an x402 endpoint action
  const isX402 = node._isX402 && node._endpoint;

  // If it's an x402 endpoint, use the specialized X402InfoContent
  if (isX402) {
    return (
      <X402InfoContent
        node={node}
        showHeader={showHeader}
        showDetailsLink={showDetailsLink}
        onDetailsClick={onDetailsClick}
      />
    );
  }

  // Fetch statistics - only for triggers and actions (logic doesn't have stats)
  const statsNodeType = nodeType === "logic" ? "action" : nodeType;
  const nodeStats = useNodeStatistics(statsNodeType, node.type);

  // Get connector logo URL if node has a connector
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const connectorLogoUrl = node.connector?.type
    ? connectorTypes.find(ct => ct.type === node.connector?.type)?.logoUrl
    : undefined;
  const connectorProp = node.connector ? { type: node.connector.type, logoUrl: connectorLogoUrl } : undefined;

  const isTrigger = nodeType === "trigger";
  const isAction = nodeType === "action";
  const isLogic = nodeType === "logic";

  const inputFields = Object.keys(node.payloadSchema?.properties || {});
  const requiredFields = node.payloadSchema?.required || [];
  const outputFields = Object.keys(node.resultSchema?.properties || {});

  // Get the color based on node type
  const iconColor = isTrigger ? "#2450FF" : isAction ? "#22c55e" : "#9333ea";

  return (
    <div className="space-y-4">
      {/* Header: Icon on left, title + type stacked on right */}
      {showHeader && (
        <div className="flex items-start gap-4">
          <NodeIconTile
            size="md"
            id={node.type}
            subject={node.name}
            context={node.description}
            logoUrl={node.logoUrl}
            connector={connectorProp}
            alt={node.name}
            color={iconColor}
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-ds-text-primary text-lg font-semibold">{node.name}</h3>
            <span className="text-ds-text-muted text-sm">{isTrigger ? "Trigger" : isAction ? "Action" : "Logic"}</span>
          </div>
        </div>
      )}
      {/* Description */}
      {showDescription && <p className="body-sm">{node.description}</p>}
      {/* Connector Requirement */}
      {showConnectorRequirement && node.connector && (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
          <IconCodeOutline18 className="h-4 w-4 flex-shrink-0" />
          <span>
            Requires{" "}
            <span className="font-medium">
              {node.connector.type.charAt(0).toUpperCase() + node.connector.type.slice(1)}
            </span>{" "}
            connector
          </span>
        </div>
      )}
      {/* Input Fields Schema */}
      {inputFields.length > 0 && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">
            Inputs{" "}
            <span className="text-ds-text-muted font-normal normal-case tracking-normal">
              ({inputFields.length}
              {requiredFields.length > 0 && `, ${requiredFields.length} required`})
            </span>
          </h4>
          <Accordion type="multiple" className="w-full">
            {inputFields.map((fieldName, index) => {
              const fieldSchema = (node.payloadSchema?.properties as Record<string, any>)?.[fieldName] || {};
              const isRequired = requiredFields.includes(fieldName);
              return (
                <SchemaField
                  key={fieldName}
                  fieldName={fieldName}
                  fieldSchema={fieldSchema}
                  isRequired={isRequired}
                  variant="input"
                  isLast={index === inputFields.length - 1}
                />
              );
            })}
          </Accordion>
        </div>
      )}
      {/* Output Fields Schema */}
      {outputFields.length > 0 && (
        <div className="border-ds-border-default border-t pt-4">
          <h4 className="field-label mb-2">
            Outputs{" "}
            <span className="text-ds-text-muted font-normal normal-case tracking-normal">({outputFields.length})</span>
          </h4>
          <Accordion type="multiple" className="w-full">
            {outputFields.map((fieldName, index) => {
              const fieldSchema = (node.resultSchema?.properties as Record<string, any>)?.[fieldName] || {};
              const isRequired = (node.resultSchema?.required || []).includes(fieldName);
              return (
                <SchemaField
                  key={fieldName}
                  fieldName={fieldName}
                  fieldSchema={fieldSchema}
                  isRequired={isRequired}
                  variant="output"
                  isLast={index === outputFields.length - 1}
                />
              );
            })}
          </Accordion>
        </div>
      )}
      {/* Accordion for Tags and Stats */}
      {((!isLogic && (nodeStats.workflowsCount > 0 || nodeStats.totalExecutions > 0)) ||
        (node.tags && node.tags.length > 0)) && (
        <Accordion type="multiple" className="border-ds-border-default w-full border-t pt-4">
          {/* Tags Accordion */}
          {node.tags && node.tags.length > 0 && (
            <AccordionItem value="tags" className={isLogic ? "border-b-0" : "border-ds-border-default border-b"}>
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="field-label">
                  Tags{" "}
                  <span className="text-ds-text-muted font-normal normal-case tracking-normal">
                    ({node.tags.length})
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-1.5 pb-2">
                  {node.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled rounded-md px-2 py-0.5 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Stats Accordion - only for triggers and actions */}
          {!isLogic && (
            <AccordionItem value="stats" className="border-b-0">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="field-label">Statistics</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-3 pb-2">
                  <div className="body-sm flex items-center gap-2">
                    <IconBellOutline18 className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {nodeStats.isLoading ? "..." : formatNumber(nodeStats.workflowsCount)} workflow
                      {nodeStats.workflowsCount !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="body-sm flex items-center gap-2">
                    <IconCrosshairsOutline18 className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {nodeStats.isLoading ? "..." : formatNumber(nodeStats.totalExecutions)} run
                      {nodeStats.totalExecutions !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {nodeStats.successRate !== null && (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <IconCheckOutline18 className="h-4 w-4 flex-shrink-0" />
                      <span>{nodeStats.isLoading ? "..." : `${nodeStats.successRate.toFixed(1)}%`} success</span>
                    </div>
                  )}

                  {isAction && nodeStats.averageLatencyMs !== null && (
                    <div className="body-sm flex items-center gap-2">
                      <IconClockOutline18 className="h-4 w-4 flex-shrink-0" />
                      <span>{nodeStats.isLoading ? "..." : `${nodeStats.averageLatencyMs?.toFixed(0)}ms`} avg</span>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}
      {/* Link to full detail page - only for triggers and actions */}
      {showDetailsLink && !isLogic && (
        <div className="pt-2">
          <Link
            href={`/integrations/${nodeType}/${node.type}`}
            target="_blank"
            rel="noreferrer"
            onClick={onDetailsClick}
          >
            <Button size="sm" className="w-full">
              <span className="flex items-center gap-2 text-xs">
                View full details
                <IconArrowUpRightOutline18 className="h-3 w-3" />
              </span>
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

interface NodeInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: ExtendedActionTriggerDefinition | null;
  nodeType: "trigger" | "action" | "logic";
}

export default function NodeInfoModal({ open, onOpenChange, node, nodeType }: NodeInfoModalProps) {
  if (!node) return null;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={node.name}
      contentClass="p-6 max-h-[90vh] overflow-y-auto max-w-2xl"
    >
      <NodeInfoContent node={node} nodeType={nodeType} onDetailsClick={() => onOpenChange(false)} />
    </ResponsiveModal>
  );
}
